import { IMeta } from '@models/IMeta';
import { TabbedFlipboardCardType } from '@models/ITabbedFlipboard';
import TabbedFlipboardCardTypeEnum from '@models/enums/TabbedFlipboardCardTypeEnum';
import NavigationCarousel from '@molecules/NavigationCarousel/NavigationCarousel';
import usePreviewMode from '@utils/hooks/usePreviewMode';
import classNames from 'classnames';
import { SwiperSlide } from 'swiper/react';
import TabbedFlipboardMediaCard from './cardTypes/TabbedFlipboardMediaCard';
import TabbedFlipboardSplitCard from './cardTypes/TabbedFlipboardSplitCard';
import TabbedFlipboardTextCard from './cardTypes/TabbedFlipboardTextCard';

interface Props {
  readonly carouselId: string;
  readonly hasMultipleTabs: boolean;
  readonly items: TabbedFlipboardCardType[];
  readonly meta?: IMeta;
  readonly tabsContainer: JSX.Element;
  readonly title?: string;
}

const getSlideKey = (item: TabbedFlipboardCardType, index: number): string => {
  const isMediaCardType =
    item.type === TabbedFlipboardCardTypeEnum.Media ||
    item.type === TabbedFlipboardCardTypeEnum.SmallMedia;
  const isTextCardType =
    item.type === TabbedFlipboardCardTypeEnum.Text ||
    item.type === TabbedFlipboardCardTypeEnum.SmallText;

  if (isMediaCardType) return `${item.headline}-${index}`;
  if (isTextCardType) return `${item.body}-${index}`;
  return `${getSlideKey(item.topCard, index)}-${getSlideKey(
    item.bottomCard,
    index
  )}`;
};

const TabbedFlipboardTab = ({
  carouselId,
  hasMultipleTabs,
  items,
  meta,
  tabsContainer,
  title,
}: Props) => {
  const isPreview = usePreviewMode();

  return (
    <>
      {isPreview && title && (
        <div className="container mb-10 flex">
          <div
            className="body-two rounded-full bg-black-100 px-4 py-1 text-white dark:bg-white dark:text-black-100"
            data-epi-property-name={meta?.editHints?.title}
          >
            {title}
          </div>
        </div>
      )}

      <NavigationCarousel
        id={carouselId}
        items={items}
        isOverflowXl={false}
        shadows
        spaceBetween={16}
        headerSlot={hasMultipleTabs ? tabsContainer : undefined}
        headerWrapperClassName={classNames({
          'xl:min-h-[2.5rem] gap-x-5 xl:gap-x-20 items-center': hasMultipleTabs,
        })}
        epiEdit={meta?.editHints?.items as string}
        dataTestId="CT-159-Tabbed-Flipboard-Navigation-Carousel"
      >
        {(itemsToDisplay) =>
          itemsToDisplay.map((item, index) => (
            <SwiperSlide
              key={getSlideKey(item, index)}
              className="stretch-card"
            >
              <div className="aspect-square">
                {(item.type === TabbedFlipboardCardTypeEnum.Media ||
                  item.type === TabbedFlipboardCardTypeEnum.SmallMedia) && (
                  <TabbedFlipboardMediaCard
                    headline={item.headline}
                    media={item.media}
                    dataTestId={`CT-159-Tabbed-Flipboard-Media-Card-${index}`}
                  />
                )}

                {(item.type === TabbedFlipboardCardTypeEnum.Text ||
                  item.type === TabbedFlipboardCardTypeEnum.SmallText) && (
                  <TabbedFlipboardTextCard
                    stat={item.stat}
                    icon={item.icon}
                    headline={'headline' in item ? item.headline : ''}
                    body={item.body}
                    dataTestId={`CT-159-Tabbed-Flipboard-Text-Card-${index}`}
                  />
                )}

                {item.type === TabbedFlipboardCardTypeEnum.Split && (
                  <TabbedFlipboardSplitCard
                    topCard={item.topCard}
                    bottomCard={item.bottomCard}
                    index={index}
                  />
                )}
              </div>
            </SwiperSlide>
          ))
        }
      </NavigationCarousel>
    </>
  );
};

export default TabbedFlipboardTab;
